// The period an export covers: a month, a quarter, a calendar year or a
// custom from–to range (ADR-0021 §1). Pure and dependency-free, so the web
// composer, the Edge Functions (Deno) and the accountant MCP resolve every
// period through this one module.
//
// Everything here is calendar dates, never instants: `transactions.booked_at`
// is a DATE, and its Europe/Berlin vs UTC question is settled at ingest.

export type CalendarPeriod =
  | { readonly kind: 'month'; readonly month: string } // YYYY-MM
  | { readonly kind: 'quarter'; readonly quarter: string } // YYYY-Qn
  | { readonly kind: 'year'; readonly year: string } // YYYY
  | { readonly kind: 'range'; readonly from: string; readonly to: string }; // YYYY-MM-DD, inclusive

export interface PeriodBounds {
  /** `2026-03`, `2026-Q2`, `2026` or `2026-01-15_2026-03-31`; feeds filenames. */
  readonly label: string;
  /** Inclusive first day, YYYY-MM-DD. */
  readonly fromDate: string;
  /** Inclusive last day, YYYY-MM-DD. */
  readonly toDate: string;
}

/**
 * A range may touch at most this many calendar months (ADR-0021 §1). The
 * count is of touched months, partial edge months included: a range export
 * builds one batch per month it touches.
 */
export const MAX_RANGE_MONTHS = 24;

/** A period the exports cannot cover; the message is fit to show the user. */
export class PeriodError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PeriodError';
  }
}

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const QUARTER_RE = /^(\d{4})-Q([1-4])$/;
const YEAR_RE = /^\d{4}$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad2 = (n: number) => String(n).padStart(2, '0');

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

const firstDay = (year: string, month: number) => `${year}-${pad2(month)}-01`;
const lastDay = (year: string, month: number) =>
  `${year}-${pad2(month)}-${pad2(daysInMonth(Number(year), month))}`;

// Months since year 0 of a YYYY-MM[-DD] string.
function monthIndex(date: string): number {
  return Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7)) - 1;
}

/** A real day of the (proleptic Gregorian) calendar, as YYYY-MM-DD. */
export function isCalendarDate(value: string): boolean {
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

/**
 * Resolves a period to its inclusive calendar dates and its label. Throws a
 * PeriodError for anything that is not a period an export may cover: a
 * malformed month, quarter or year, a non-calendar date, an inverted range,
 * or a range touching more than MAX_RANGE_MONTHS months.
 */
export function periodBounds(period: CalendarPeriod): PeriodBounds {
  switch (period.kind) {
    case 'month': {
      const match = MONTH_RE.exec(period.month);
      if (!match) throw new PeriodError(`${period.month} is not a month (YYYY-MM)`);
      const [, year = '', month = ''] = match;
      return {
        label: period.month,
        fromDate: firstDay(year, Number(month)),
        toDate: lastDay(year, Number(month)),
      };
    }
    case 'quarter': {
      const match = QUARTER_RE.exec(period.quarter);
      if (!match) {
        throw new PeriodError(`${period.quarter} is not a quarter (YYYY-Q1 … YYYY-Q4)`);
      }
      const [, year = '', quarter = ''] = match;
      const lastMonth = Number(quarter) * 3;
      return {
        label: period.quarter,
        fromDate: firstDay(year, lastMonth - 2),
        toDate: lastDay(year, lastMonth),
      };
    }
    case 'year': {
      if (!YEAR_RE.test(period.year)) throw new PeriodError(`${period.year} is not a year (YYYY)`);
      return {
        label: period.year,
        fromDate: firstDay(period.year, 1),
        toDate: lastDay(period.year, 12),
      };
    }
    case 'range': {
      for (const date of [period.from, period.to]) {
        if (!isCalendarDate(date)) {
          throw new PeriodError(`${date} is not a calendar date (YYYY-MM-DD)`);
        }
      }
      // Same-width ISO dates compare chronologically as strings.
      if (period.from > period.to) {
        throw new PeriodError(`the range starts (${period.from}) after it ends (${period.to})`);
      }
      const months = monthIndex(period.to) - monthIndex(period.from) + 1;
      if (months > MAX_RANGE_MONTHS) {
        throw new PeriodError(
          `the range covers ${months} months; at most ${MAX_RANGE_MONTHS} are allowed`,
        );
      }
      return { label: `${period.from}_${period.to}`, fromDate: period.from, toDate: period.to };
    }
  }
}

/**
 * `YYYY-MM`, `YYYY-Qn` or `YYYY` as a period; null for anything else. The
 * one grammar of the `period` parameter, shared by the web URL, the Edge
 * Functions and the accountant MCP.
 */
export function parsePeriodString(value: string): CalendarPeriod | null {
  if (MONTH_RE.test(value)) return { kind: 'month', month: value };
  if (QUARTER_RE.test(value)) return { kind: 'quarter', quarter: value };
  if (YEAR_RE.test(value)) return { kind: 'year', year: value };
  return null;
}

export interface PeriodQuery {
  readonly period?: string | null;
  readonly from?: string | null;
  readonly to?: string | null;
}

/**
 * Reads a request's period: `period` as month, quarter or year, or `from` +
 * `to` (inclusive calendar dates). Empty values count as absent. Returns a
 * period periodBounds accepts; throws a PeriodError with a message fit for
 * the caller otherwise. `names` are the caller's own names for from and to
 * (the accountant MCP calls them from_date / to_date), used in refusals.
 */
export function parsePeriodQuery(
  input: PeriodQuery,
  names: { readonly from: string; readonly to: string } = { from: 'from', to: 'to' },
): CalendarPeriod {
  const period = input.period || null;
  const from = input.from || null;
  const to = input.to || null;
  if (period !== null && (from !== null || to !== null)) {
    throw new PeriodError(`pass either period or ${names.from} and ${names.to}, not both`);
  }
  let parsed: CalendarPeriod;
  if (period !== null) {
    const named = parsePeriodString(period);
    if (named === null) {
      throw new PeriodError(`${period} is not a period (YYYY-MM, YYYY-Qn or YYYY)`);
    }
    parsed = named;
  } else if (from !== null && to !== null) {
    parsed = { kind: 'range', from, to };
  } else if (from !== null || to !== null) {
    throw new PeriodError(`pass ${names.from} and ${names.to} together`);
  } else {
    throw new PeriodError(
      `pass period (YYYY-MM, YYYY-Qn or YYYY) or ${names.from} and ${names.to} (YYYY-MM-DD)`,
    );
  }
  periodBounds(parsed);
  return parsed;
}

/**
 * The request form of a period, the inverse of parsePeriodQuery. Throws a
 * PeriodError for a period parsePeriodQuery would refuse.
 */
export function periodQuery(period: CalendarPeriod): Record<string, string> {
  const { label } = periodBounds(period);
  if (period.kind === 'range') return { from: period.from, to: period.to };
  return { period: label };
}

/** One calendar month of a period, as a range export builds it (ADR-0021 §3). */
export interface MonthSlice {
  /** YYYY-MM: the calendar month, and the month's folder in a range ZIP. */
  readonly month: string;
  /** The period label of this month's export batch. */
  readonly label: string;
  /** Inclusive first day, YYYY-MM-DD. */
  readonly fromDate: string;
  /** Inclusive last day, YYYY-MM-DD. */
  readonly toDate: string;
}

/**
 * The calendar months a period touches, in order, each clipped to the
 * period. Throws a PeriodError for a period periodBounds refuses.
 */
export function splitIntoMonths(period: CalendarPeriod): readonly MonthSlice[] {
  const { fromDate, toDate } = periodBounds(period);
  const slices: MonthSlice[] = [];
  for (let index = monthIndex(fromDate); index <= monthIndex(toDate); index += 1) {
    const year = String(Math.floor(index / 12)).padStart(4, '0');
    const month = (index % 12) + 1;
    const calendarMonth = `${year}-${pad2(month)}`;
    const wholeFrom = firstDay(year, month);
    const wholeTo = lastDay(year, month);
    // Same-width ISO dates compare chronologically as strings.
    const sliceFrom = fromDate > wholeFrom ? fromDate : wholeFrom;
    const sliceTo = toDate < wholeTo ? toDate : wholeTo;
    const whole = sliceFrom === wholeFrom && sliceTo === wholeTo;
    slices.push({
      month: calendarMonth,
      // A partial edge month says so in the history: its batch is labelled
      // like the range it covers, never as the whole month.
      label: whole ? calendarMonth : `${sliceFrom}_${sliceTo}`,
      fromDate: sliceFrom,
      toDate: sliceTo,
    });
  }
  return slices;
}

/**
 * The range a month batch of a range export was built for, as recorded in
 * export_batches.filter_spec.range (ADR-0021 §4). Inclusive dates.
 */
export interface RangeSpec {
  readonly label: string;
  readonly from: string;
  readonly to: string;
}

/**
 * Reads the range a month export claims to belong to, as sent by a client.
 * It must be a period the exports accept — its label naming exactly its
 * dates (`2026-Q1`, `2026`, `2026-03`, or `from_to`) — and contain the month.
 * Throws a PeriodError otherwise, so a batch never records a range it could
 * not have come from.
 */
export function parseRangeSpec(
  value: unknown,
  month: { readonly fromDate: string; readonly toDate: string },
): RangeSpec {
  const raw = value as Record<string, unknown> | null;
  if (
    typeof raw !== 'object' ||
    raw === null ||
    typeof raw.label !== 'string' ||
    typeof raw.from !== 'string' ||
    typeof raw.to !== 'string'
  ) {
    throw new PeriodError('range must be an object with label, from and to');
  }
  const { label, from, to } = raw;
  const named = parsePeriodString(label);
  const bounds = periodBounds(named ?? { kind: 'range', from, to });
  if (bounds.label !== label || bounds.fromDate !== from || bounds.toDate !== to) {
    throw new PeriodError(`range label ${label} does not match ${from} – ${to}`);
  }
  if (month.fromDate < from || month.toDate > to) {
    throw new PeriodError(
      `the month ${month.fromDate} – ${month.toDate} lies outside the range ${label}`,
    );
  }
  return { label, from, to };
}
